/**
 * SafetyIncidentList — color-coded incident feed for the Safety & Risk
 * Management Command Center. Each row is tinted by state and stamped
 * with a colored region pill so a Safety officer can triage at a glance.
 *
 * Fields shown: date/time · type · reporter (role+position) · vehicle ·
 * state · region · city · police case# · insurance carrier · claim# ·
 * roadside flag · photo count · status.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { AlertTriangle, RefreshCw, Camera, Shield, Phone } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Distinct hue per US state — hash-based, stable across renders.
const stateColor = (s) => {
  if (!s) return '#3f3f46';
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return `hsl(${h}, 55%, 22%)`;
};

const TYPE_TONE = {
  collision: 'bg-red-700',
  minor_accident: 'bg-red-600',
  vandalism: 'bg-purple-700',
  theft: 'bg-purple-800',
  roadside_towing: 'bg-amber-700',
  roadside_battery: 'bg-amber-600',
  roadside_lockout: 'bg-amber-600',
  roadside_flat_tire: 'bg-amber-600',
  roadside_other: 'bg-amber-500',
  other: 'bg-zinc-700',
};

export default function SafetyIncidentList({ regions = [] }) {
  const [rows, setRows] = useState([]);
  const [stateFilter, setStateFilter] = useState('__ALL__');
  const [statusFilter, setStatusFilter] = useState('__ALL__');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (stateFilter !== '__ALL__') q.set('state', stateFilter);
      if (statusFilter !== '__ALL__') q.set('status', statusFilter);
      const res = await fetch(`${API}/api/safety-incidents?${q.toString()}`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setRows(await res.json());
    } catch (e) {
      toast.error(`Safety incidents load failed: ${e.message}`);
    } finally { setLoading(false); }
  }, [stateFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const regionColorById = useMemo(() => (regions || []).reduce((acc, r) => {
    acc[r.region_id] = r.color || '#C9A962';
    return acc;
  }, {}), [regions]);

  const states = useMemo(() => {
    const s = new Set(rows.map((r) => r.state).filter(Boolean));
    return Array.from(s).sort();
  }, [rows]);

  const setStatus = async (incidentId, next) => {
    const res = await fetch(`${API}/api/safety-incidents/${incidentId}/status?status=${next}`, {
      method: 'PATCH', credentials: 'include',
    });
    if (res.ok) { toast.success('Status updated'); load(); }
    else toast.error('Update failed');
  };

  return (
    <Card className="bg-zinc-900 border-2 border-red-500/40 p-4" data-testid="safety-incident-list">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-red-500" />
          <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            SAFETY &amp; RISK · INCIDENT FEED
          </h3>
          <Badge className="bg-red-700 text-white">{rows.length}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Select value={stateFilter} onValueChange={setStateFilter}>
            <SelectTrigger className="h-8 w-[120px] bg-zinc-950 border-zinc-700 text-white text-[11px]" data-testid="incident-list-state">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700 text-white">
              <SelectItem value="__ALL__">All states</SelectItem>
              {states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-8 w-[140px] bg-zinc-950 border-zinc-700 text-white text-[11px]" data-testid="incident-list-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700 text-white">
              <SelectItem value="__ALL__">All statuses</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_review">In review</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} disabled={loading}
                  className="h-8 border-zinc-700 text-zinc-300" data-testid="incident-list-refresh">
            <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-zinc-500 italic py-4 text-center" data-testid="incident-list-empty">
          No incidents match the current filters.
        </p>
      ) : (
        <div className="space-y-1.5 max-h-[520px] overflow-y-auto pr-1">
          {rows.map((r) => (
            <div
              key={r.incident_id}
              className="rounded border-l-4 border-zinc-700 bg-zinc-950/40 p-2.5 flex items-center gap-2 flex-wrap"
              style={{ backgroundColor: stateColor(r.state), borderLeftColor: regionColorById[r.region_id] || '#C9A962' }}
              data-testid={`incident-row-${r.incident_id}`}
            >
              <Badge className={`text-[10px] font-bold text-white ${TYPE_TONE[r.incident_type] || 'bg-zinc-700'}`}>
                {(r.incident_type || 'other').replace(/_/g, ' ')}
              </Badge>
              <span className="text-[11px] text-zinc-300 font-mono">
                {r.created_at ? new Date(r.created_at).toLocaleString([], { month: '2-digit', day: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
              </span>
              <span className="text-[11px] text-white truncate max-w-[180px]" title={r.reporter_name}>
                <span className="text-zinc-400">by </span>{r.reporter_name}{r.reporter_position ? ` · ${r.reporter_position}` : ''}
              </span>
              {r.license_plate && (
                <Badge className="bg-zinc-800 text-[10px] font-mono text-white">{r.license_plate}</Badge>
              )}
              <Badge className="bg-zinc-800 text-white text-[10px] font-mono">
                {r.state || '—'}
              </Badge>
              {r.region_code && (
                <Badge className="text-[10px] font-bold text-black"
                       style={{ backgroundColor: regionColorById[r.region_id] || '#C9A962' }}>
                  {r.region_code}
                </Badge>
              )}
              {r.city && <span className="text-[11px] text-zinc-300">{r.city}</span>}
              {r.police_case_number && (
                <Badge className="bg-blue-700 text-white text-[10px]" title="Police case #">
                  Police #{r.police_case_number}
                </Badge>
              )}
              {r.claim_number && (
                <Badge className="bg-emerald-700 text-white text-[10px]" title="Claim #">
                  Claim #{r.claim_number}
                </Badge>
              )}
              {r.roadside_requested && (
                <Badge className="bg-amber-600 text-white text-[10px] inline-flex items-center gap-1">
                  <Phone className="h-2.5 w-2.5" /> Roadside
                </Badge>
              )}
              {(r.photos?.length || 0) > 0 && (
                <Badge className="bg-zinc-700 text-white text-[10px] inline-flex items-center gap-1">
                  <Camera className="h-2.5 w-2.5" /> {r.photos.length}
                </Badge>
              )}
              <div className="flex-1 min-w-[80px] text-[11px] text-zinc-300 truncate">
                {r.description}
              </div>
              <Select value={r.status || 'open'} onValueChange={(v) => setStatus(r.incident_id, v)}>
                <SelectTrigger className="h-7 w-[110px] bg-zinc-950 border-zinc-700 text-white text-[10px]" data-testid={`incident-status-${r.incident_id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700 text-white">
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_review">In review</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
